import type { ReactElement } from "react"
import { useCallback } from "react"
import type { DomainCheck } from "@onestore/api/domainSearch"
import type { DomainBundlePricing } from "@onestore/api/types"
import type { ButtonAtmProps } from "@onestore/hel/dist/components/atoms/ButtonAtm"
import LabelAtm from "@onestore/hel/dist/components/atoms/LabelAtm"
import DomainSearchResultItemMol from "@onestore/hel/dist/components/molecules/DomainSearchResultItemMol"
import { isResponsePending } from "@onestore/onestore-store-common/http"
import _t from "@core/i18n"
import { formatPrice } from "@core/pricing"
import type { SelectedBundle } from "@gatsby-plugin-bonus/types"
import { getBundleAdditionalBox } from "@gatsby-plugin-domain-search/components/CategoryResults/BundleItem"
import useAXDomainBundleQuery from "@gatsby-plugin-domain-search/hooks/useAXDomainBundleQuery"
import useDomainActionsDispatch from "@gatsby-plugin-domain-search/hooks/useDomainActionsDispatch"
import type { DomainSearch } from "@gatsby-plugin-domain-search/types"
import { Text } from "@ionos-web-design-system/react"
import {
  buttonColor,
  searchResultExtensionColor,
} from "~/configs/checkoutConfig"
import { usePriceTypeContext } from "~/context/PriceTypeContext"
import isEmpty from "~/lib/isEmpty"
import { getBasketRequestState } from "~/store/basket/selectors"
import { useAppSelector } from "~/store/hooks"

type BundleRowProps = {
  pricing: null | DomainBundlePricing
  domains: DomainCheck.Result[]
  buttonId: string
  isLoading: boolean
  isPartialBundleInBasket: boolean
  buttonDataTestId?: string
  onDomainSelected?: (domainResult: DomainCheck.Result) => void
  onBundleSelected?: (bundle: SelectedBundle) => void
  selectedFqdns?: string[]
  onNextStepClick?: () => void
}

export default function BundleRow({
  isLoading,
  buttonId,
  domains,
  pricing,
  isPartialBundleInBasket,
  buttonDataTestId,
  onDomainSelected,
  onBundleSelected,
  selectedFqdns,
  onNextStepClick,
  ...restProps
}: BundleRowProps): ReactElement | null {
  const { addDomainsToBasket } = useDomainActionsDispatch()
  const axBundleData = useAXDomainBundleQuery()

  const { isNettoPriceType } = usePriceTypeContext()
  const isAnyBasketPending = isResponsePending(
    useAppSelector(getBasketRequestState)
  )

  const insertedDomains: DomainSearch.DomainFqdnWithPlan[] = domains.map(
    (domain): DomainSearch.DomainFqdnWithPlan => {
      return {
        fqdn: domain.fqdn,
        plan: domain.plan_id,
      }
    }
  )

  const mainDomains = insertedDomains.slice(0, 1)
  const childDomains = insertedDomains.slice(1)

  const onClick = useCallback(() => {
    if (isLoading || isAnyBasketPending) {
      return
    }

    addDomainsToBasket(mainDomains, childDomains, buttonId, false)
  }, [insertedDomains, isPartialBundleInBasket, isLoading, isAnyBasketPending])

  if (pricing === null) {
    return null
  }

  const period = pricing.period_value
    ? ` / ${_t(`periodName.searchDomainPrice.${pricing.period_type}`, {
        smart_count: pricing.period_value,
      })}`
    : ""

  const price = isNettoPriceType
    ? pricing.register_price
    : pricing.register_price_gross

  if (onDomainSelected && onBundleSelected) {
    const isSelected = domains.every((domain) =>
      selectedFqdns?.includes(domain.fqdn)
    )

    const selectionButtonText = isSelected
      ? _t("preBasket.nextStep")
      : _t("domainSearch.button.add")

    const handleSelectionButtonClick = (): void => {
      if (isSelected) {
        onNextStepClick?.()

        return
      }

      const mainDomain = !isEmpty(mainDomains) ? mainDomains[0] : null

      if (
        onBundleSelected &&
        mainDomain &&
        domains.every((domain) => domain.plan_id !== null)
      ) {
        onBundleSelected({ mainDomain, childDomains, pricing })

        return
      }

      domains.forEach((domain) => {
        if (domain.plan_id !== null) {
          onDomainSelected(domain)
        }
      })
    }

    return (
      <DomainSearchResultItemMol
        domain={domains.map((domain) => ({
          name: domain.name,
          extension: `.${domain.extension}`,
          extensionColor: searchResultExtensionColor,
        }))}
        price={
          <Text weight="black">
            {_t("domainSearch.total")} {formatPrice(price)}{" "}
            <Text variant="bodyXs">{period}</Text>
          </Text>
        }
        hasSuccess={isSelected}
        caption={
          <LabelAtm size="small" variant="notifyLight">
            {axBundleData.title || _t("domainSearch.groupHeaderText")}
          </LabelAtm>
        }
        button={
          {
            text: selectionButtonText,
            title: selectionButtonText,
            onClick: handleSelectionButtonClick,
            dataTestId: buttonDataTestId,
          } as ButtonAtmProps
        }
        additionalBox={getBundleAdditionalBox(axBundleData)}
        overriddenSuccessButtonColor={buttonColor}
        {...restProps}
      />
    )
  }

  return (
    <DomainSearchResultItemMol
      domain={domains.map((domain) => ({
        name: domain.name,
        extension: `.${domain.extension}`,
        extensionColor: searchResultExtensionColor,
      }))}
      price={
        <Text weight="black">
          {_t("domainSearch.total")} {formatPrice(price)}{" "}
          <Text variant="bodyXs">{period}</Text>
        </Text>
      }
      isLoading={isLoading}
      button={
        {
          text: _t("domainSearch.button.addToCart"),
          title: _t("domainSearch.button.addToCart"),
          onClick,
          dataTestId: buttonDataTestId,
          isDisabled: isAnyBasketPending,
        } as ButtonAtmProps
      }
      caption={
        <LabelAtm size="small" variant="notifyLight">
          {axBundleData.title || _t("domainSearch.groupHeaderText")}
        </LabelAtm>
      }
      additionalBox={getBundleAdditionalBox(axBundleData)}
      overriddenSuccessButtonColor={buttonColor}
      {...restProps}
    />
  )
}
